'use client';

import { FormField } from '@/features/forms/FormPrimitives';
import readHubMessage from '@/features/hub/shared/readHubMessage';
import { clientFetch } from '@/lib/api/clientFetch';
import Link from 'next/link';
import { useEffect, useState } from 'react';
import { canCurrentUserManageHubTeam } from '../teamRoles';

export default function HubTeamJoinPanel({
  team,
  currentUser,
  loginRedirectPath = '',
  messages = {},
}) {
  const [joinStatus, setJoinStatus] = useState(
    currentUser ? 'loading' : 'none',
  );
  const [requestMessage, setRequestMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const isModerator = canCurrentUserManageHubTeam(team, currentUser);
  const msg = (path, fallback = '') => readHubMessage(messages, path, fallback);
  const loginHref = `/login?redirect=${encodeURIComponent(
    loginRedirectPath || `/hub/teams/${team.teamNumber}`,
  )}`;

  useEffect(() => {
    let isMounted = true;

    const loadJoinState = async () => {
      if (!currentUser || isModerator) {
        setJoinStatus('none');
        return;
      }

      try {
        const response = await clientFetch(
          `/api/hub/teams/${team.teamNumber}/join`,
        );

        if (!isMounted) {
          return;
        }

        setJoinStatus(response?.status || 'none');
        setRequestMessage(response?.message || '');
      } catch (error) {
        if (!isMounted) {
          return;
        }

        setJoinStatus('none');
        setErrorMessage(
          error?.data?.message ||
            error?.message ||
            msg(
              'teams.joinPanel.errors.loadStatus',
              'Unable to load team request status.',
            ),
        );
      }
    };

    loadJoinState();

    return () => {
      isMounted = false;
    };
  }, [currentUser, isModerator, team.teamNumber]);

  const submitJoinRequest = async () => {
    setErrorMessage('');
    setIsSubmitting(true);

    try {
      const response = await clientFetch(
        `/api/hub/teams/${team.teamNumber}/join`,
        {
          method: 'POST',
          headers: {
            'content-type': 'application/json',
          },
          body: JSON.stringify({
            message: requestMessage.trim(),
          }),
        },
      );

      setJoinStatus(response?.status || 'pending');
      setRequestMessage(response?.message || requestMessage);
    } catch (error) {
      setErrorMessage(
        error?.data?.message ||
          error?.message ||
          msg(
            'teams.joinPanel.errors.request',
            'Unable to send the team join request.',
          ),
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const cancelJoinRequest = async () => {
    setErrorMessage('');
    setIsSubmitting(true);

    try {
      await clientFetch(`/api/hub/teams/${team.teamNumber}/join`, {
        method: 'DELETE',
      });

      setJoinStatus('none');
    } catch (error) {
      setErrorMessage(
        error?.data?.message ||
          error?.message ||
          msg(
            'teams.joinPanel.errors.cancel',
            'Unable to cancel the team join request.',
          ),
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const isMember = joinStatus === 'member' || joinStatus === 'approved';
  const hasInvite = joinStatus === 'invited';

  return (
    <section className="app-panel hub-action-panel hub-action-panel--teams">
      <div className="hub-action-panel__header">
        <div>
          <p className="hub-section__eyebrow hub-section__eyebrow--teams">
            {msg('teams.joinPanel.eyebrow', 'Team membership')}
          </p>
          <h2>{msg('teams.joinPanel.title', 'Request to join this team')}</h2>
        </div>
        {isMember ? (
          <span className="hub-pill hub-pill--games">
            {msg('teams.joinPanel.memberBadge', 'Member')}
          </span>
        ) : hasInvite ? (
          <span className="hub-pill hub-pill--games">
            {msg('teams.joinPanel.invitedBadge', 'Invited')}
          </span>
        ) : joinStatus === 'pending' ? (
          <span className="hub-pill hub-pill--warning">
            {msg('teams.joinPanel.pendingBadge', 'Pending')}
          </span>
        ) : null}
      </div>

      {!currentUser ? (
        <div className="hub-inline-stack">
          <p className="shop-panel__meta">
            {msg(
              'teams.joinPanel.loginHint',
              'Sign in to request a spot on this team.',
            )}
          </p>
          <div className="account-form__actions">
            <Link
              href={loginHref}
              className="app-button app-button--primary gap-2"
            >
              <span>🔐</span>
              {msg('teams.joinPanel.loginCta', 'Log in to request access')}
            </Link>
          </div>
        </div>
      ) : isModerator ? (
        <p className="shop-panel__meta">
          {msg(
            'teams.joinPanel.ownerHint',
            'You manage this team. Review membership requests and roster changes below.',
          )}
        </p>
      ) : joinStatus === 'loading' ? (
        <p className="shop-panel__meta">
          {msg(
            'teams.joinPanel.loadingStatus',
            'Checking your membership request...',
          )}
        </p>
      ) : (
        <div className="hub-inline-stack">
          {isMember ? (
            <p className="shop-panel__meta">
              {msg(
                'teams.joinPanel.memberHint',
                'You are already part of this team roster.',
              )}
            </p>
          ) : joinStatus === 'pending' ? (
            <p className="shop-panel__meta">
              {msg(
                'teams.joinPanel.pendingHint',
                'Your request is pending review from the team owner.',
              )}
            </p>
          ) : hasInvite ? (
            <>
              <p className="shop-panel__meta">
                {msg(
                  'teams.joinPanel.invitedHint',
                  'You already have a direct invite from this team. Review it from your profile.',
                )}
              </p>

              <div className="account-form__actions">
                <Link
                  href="/hub/profile"
                  className="app-button app-button--ghost"
                >
                  {msg(
                    'teams.joinPanel.reviewInviteCta',
                    'Review invite on profile',
                  )}
                </Link>
              </div>
            </>
          ) : joinStatus === 'rejected' ? (
            <p className="shop-panel__meta">
              {msg(
                'teams.joinPanel.rejectedHint',
                'Your last request was declined. You can send a fresh request if the team is still open.',
              )}
            </p>
          ) : (
            <>
              <p className="shop-panel__meta">
                {msg(
                  'teams.joinPanel.noneHint',
                  'Introduce yourself before asking to join this team.',
                )}
              </p>
              <FormField
                as="textarea"
                label={msg(
                  'teams.joinPanel.messageLabel',
                  'Message to the owner',
                )}
                rows={4}
                value={requestMessage}
                onChange={(event) => setRequestMessage(event.target.value)}
                placeholder={msg(
                  'teams.joinPanel.messagePlaceholder',
                  'Share your role, goals, availability, or why you want to join.',
                )}
                inputClassName="ps-3"
                controlWrapClassName="mt-1"
              />
            </>
          )}

          {errorMessage ? (
            <div className="auth-message auth-message--error">
              {errorMessage}
            </div>
          ) : null}

          <div className="account-form__actions">
            {joinStatus === 'pending' ? (
              <button
                type="button"
                className="app-button app-button--ghost"
                onClick={cancelJoinRequest}
                disabled={isSubmitting}
              >
                {isSubmitting
                  ? msg('teams.joinPanel.updating', 'Updating...')
                  : msg('teams.joinPanel.cancelCta', 'Cancel request')}
              </button>
            ) : !isMember ? (
              <button
                type="button"
                className="app-button app-button--primary"
                onClick={submitJoinRequest}
                disabled={isSubmitting}
              >
                {isSubmitting
                  ? msg('teams.joinPanel.sending', 'Sending...')
                  : msg('teams.joinPanel.requestCta', 'Request to join')}
              </button>
            ) : null}
          </div>
        </div>
      )}
    </section>
  );
}
